import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'

import { RoutingPaths } from 'app/routes/routes.ts'

import { parseHotelStars } from 'entities/tour/libs/parseHotelStars.ts'
import selectionStore from 'entities/tour/models/selection-store/selection-store.ts'

import { Hotel } from 'shared/api/tours/search'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList.ts'
import GhostImage from 'shared/images/ghost.png'
import { dateFormat } from 'shared/lib/dateFormat.ts'
import { priceShow } from 'shared/lib/priceShow.ts'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { Badge } from 'shared/ui/badge'
import { Carousel } from 'shared/ui/carousel'
import { StarRating } from 'shared/ui/star-rating'

import { SearchResultCardParams } from './components/search-result-card-params'
import s from './styles.module.scss'

interface Props {
  item?: Hotel
}

export const SearchResultCard = ({ item }: Props) => {
  const { t } = useTranslation()
  const firstTour = item?.tours?.at(0)
  const navigate = useNavigate()

  const preparedImages = useMemo(() => {
    return (
      item?.hotel_images
        ?.map((item) => ({
          id: item.sort_index,
          image: item.src,
          image_fullpath: item.src,
        }))
        .slice(0, 10) || []
    )
  }, [item])

  const locationText = `${item?.region_name}, ${item?.state_name}`
  const datesText = `${dateFormat(
    firstTour?.checkin_date,
    DATE_FORMAT_LIST.DATE_MONTH_SHORT,
  )} - ${dateFormat(firstTour?.checkout_date, DATE_FORMAT_LIST.DATE_MONTH_SHORT)}`
  const nightsText = `${t('tours_block.nights_and_flight', {
    nights: Number(firstTour?.nights) || 0,
  })}`

  const starsNumber = parseHotelStars(item?.hotel_stars || '')

  const handleCardClick = () => {
    selectionStore.resetRoomFilters()

    navigate(`${RoutingPaths.tours.tourDetail.path(item?.id || '')}`)
  }

  return (
    <div
      className={s.searchResultCard}
      onClick={handleCardClick}
    >
      <Carousel
        dotsVariant="custom"
        images={preparedImages}
        errorImage={GhostImage}
        showCounter
      />

      {item?.is_recommended && (
        <div className={s.badge}>
          <Badge>
            <AppIcon
              id={AllIcons.LOGO}
              width={12}
              height={12}
            />
            {t('tours_block.recommended')}
            <AppIcon
              id={AllIcons.LIKE}
              width={12}
              height={12}
            />
          </Badge>
        </div>
      )}

      <div className={s.searchResultCardContent}>
        <div className={s.header}>
          {starsNumber > 0 && <StarRating rating={starsNumber} />}

          {/*
            <Badge
              color="violet"
              className={s.departureBadge}
            >
            <AppIcon
              id={AllIcons.PLANE}
              width={18}
              height={18}
            />
            {`${t('tours_block.departure_from')} ${item?.town_from?.name}`}
          </Badge> */}
        </div>
        <div className={s.top}>
          <div className={s.resultText}>
            <p className={clsx('extra-bold-16', s.tourName)}>
              {item?.hotel_name}
            </p>
            <p className={clsx('medium-12', s.tourInfo)}>{locationText}</p>
          </div>
          {Boolean(item?.services?.length) && (
            <div className={s.paramsLine}>
              {item?.services?.map((service) => (
                <SearchResultCardParams
                  key={service.name}
                  icon={service.icon}
                  text={service.name}
                />
              ))}
            </div>
          )}
        </div>

        <div className={s.divider} />

        <div className={s.right}>
          <span className={clsx('semi-bold-12', s.dates)}>{datesText}</span>
          <span className={clsx('medium-12', s.nights)}>{nightsText}</span>
        </div>

        <div className={s.divider} />

        <div className={s.bottom}>
          <div className={s.left}>
            <div className={clsx('extra-bold-16', s.price)}>
              {t('from_price', { price: priceShow(String(firstTour?.price)) })}
            </div>
            <p className={clsx('semi-bold-14', s.dollar)}>
              (~&#36;
              {priceShow(String(Math.ceil(Number(firstTour?.price_usd || 0))))})
            </p>

            {/*<MplusXText*/}
            {/*  tranche="1000"*/}
            {/*  disableReturn*/}
            {/*/>*/}
          </div>
        </div>
      </div>
    </div>
  )
}
